// AllergenCatalogTab.js
// Admin curation of the allergen catalog: non-drug allergens (Shellfish,
// Latex, ...) and drug-class handles (Penicillins, ACE inhibitors, ...).
//
// Mirrors LocationsTab's shape: inline add/edit form + table with neutral
// Edit / caution Retire actions. Soft-retire only (no hard delete) — rows may
// be referenced by patient_allergies.allergen_catalog_id.
//
// The kind/shape invariant matches the backend + DB constraint:
//   kind='non_drug'   -> category required, no EPC class
//   kind='drug_class' -> EPC class required, no category
// The form shows only the relevant field per kind, so it can't build a
// malformed row. Aliases are edited as add/remove chips.

import React, { useState, useEffect, useCallback } from 'react';
import api from '../../services/api';
import Button from '../../components/Button';
import { IconX } from '@tabler/icons-react';
import { styles } from './settingsShared';

const KIND_OPTIONS = [
  { value: 'non_drug',   label: 'Non-drug allergen' },
  { value: 'drug_class', label: 'Drug class' },
];
const CATEGORY_OPTIONS = ['food', 'environmental', 'latex', 'other'];

const KIND_LABEL = { non_drug: 'Non-drug', drug_class: 'Drug class' };
const CATEGORY_LABEL = {
  food: 'Food', environmental: 'Environmental', latex: 'Latex', other: 'Other',
};

const EMPTY_FORM = {
  label: '',
  kind: 'non_drug',
  category: 'food',       // sensible default for the default kind
  epc_class_name: '',
  aliases: [],
  is_active: true,
  sort_order: 0,
};

export default function AllergenCatalogTab() {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [includeInactive, setIncludeInactive] = useState(false);

  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [aliasDraft, setAliasDraft] = useState('');
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get(
        `/admin/allergens/catalog?include_inactive=${includeInactive}`
      );
      setEntries(res.data || []);
      setError('');
    } catch (e) {
      setError(e?.response?.data?.detail || 'Failed to load allergen catalog.');
    } finally {
      setLoading(false);
    }
  }, [includeInactive]);

  useEffect(() => { load(); }, [load]);

  const openAdd = () => {
    setEditingId(null);
    setForm(EMPTY_FORM);
    setAliasDraft('');
    setFormError('');
    setShowForm(true);
  };

  const openEdit = (e) => {
    setEditingId(e.id);
    setForm({
      label: e.label || '',
      kind: e.kind || 'non_drug',
      category: e.category || (e.kind === 'non_drug' ? 'food' : ''),
      epc_class_name: e.epc_class_name || '',
      aliases: e.aliases ? [...e.aliases] : [],
      is_active: e.is_active ?? true,
      sort_order: e.sort_order ?? 0,
    });
    setAliasDraft('');
    setFormError('');
    setShowForm(true);
  };

  const cancelForm = () => {
    setShowForm(false);
    setEditingId(null);
    setForm(EMPTY_FORM);
    setAliasDraft('');
    setFormError('');
  };

  // Switching kind resets the kind-specific field so the row can't carry both.
  const selectKind = (kind) => {
    setForm(f => ({
      ...f,
      kind,
      category: kind === 'non_drug' ? (f.category || 'food') : '',
      epc_class_name: kind === 'drug_class' ? f.epc_class_name : '',
    }));
  };

  // Alias chips
  const addAlias = () => {
    const t = aliasDraft.trim();
    if (!t) return;
    if (form.aliases.some(a => a.toLowerCase() === t.toLowerCase())) {
      setAliasDraft('');
      return;
    }
    setForm(f => ({ ...f, aliases: [...f.aliases, t] }));
    setAliasDraft('');
  };
  const removeAlias = (alias) => {
    setForm(f => ({ ...f, aliases: f.aliases.filter(a => a !== alias) }));
  };
  const onAliasKeyDown = (e) => {
    if (e.key === 'Enter') { e.preventDefault(); addAlias(); }
  };

  const validate = () => {
    if (!form.label.trim()) { setFormError('Label is required.'); return false; }
    if (form.kind === 'non_drug' && !form.category) {
      setFormError('A non-drug allergen needs a category.'); return false;
    }
    if (form.kind === 'drug_class' && !form.epc_class_name.trim()) {
      setFormError('A drug class needs an EPC class name.'); return false;
    }
    return true;
  };

  const handleSave = async () => {
    if (!validate()) return;
    setSaving(true);
    setFormError('');
    const payload = {
      label: form.label.trim(),
      kind: form.kind,
      category: form.kind === 'non_drug' ? form.category : null,
      epc_class_name: form.kind === 'drug_class' ? form.epc_class_name.trim() : null,
      aliases: form.aliases,
      is_active: form.is_active,
      sort_order: parseInt(form.sort_order, 10) || 0,
    };
    try {
      if (editingId) {
        await api.put(`/admin/allergens/catalog/${editingId}`, payload);
      } else {
        await api.post('/admin/allergens/catalog', payload);
      }
      cancelForm();
      load();
    } catch (e) {
      setFormError(e?.response?.data?.detail || 'Failed to save allergen.');
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (e) => {
    try {
      await api.put(`/admin/allergens/catalog/${e.id}`, { is_active: !e.is_active });
      load();
    } catch { /* surfaced on reload if it failed */ }
  };

  return (
    <div>
      <div style={styles.tabHeader}>
        <h2 style={styles.sectionTitle}>Allergen Catalog</h2>
        <Button onClick={showForm ? cancelForm : openAdd}
          variant={showForm ? 'secondary' : 'primary'}>
          {showForm ? 'Cancel' : '+ Add Allergen'}
        </Button>
      </div>

      {showForm && (
        <div style={styles.form}>
          <div style={styles.formTitle}>{editingId ? 'Edit Allergen' : 'New Allergen'}</div>
          {formError && <div style={styles.errorText}>{formError}</div>}

          <div style={styles.formGrid}>
            {/* Label */}
            <div style={styles.fieldGroup}>
              <label style={styles.label}>Label *</label>
              <input
                style={styles.input}
                value={form.label}
                onChange={e => setForm({ ...form, label: e.target.value })}
                placeholder="e.g. Shellfish, Penicillins"
              />
            </div>

            {/* Kind */}
            <div style={styles.fieldGroup}>
              <label style={styles.label}>Kind</label>
              <select
                style={styles.input}
                value={form.kind}
                onChange={e => selectKind(e.target.value)}
              >
                {KIND_OPTIONS.map(o => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
            </div>

            {/* Category (non_drug) OR EPC class (drug_class) — kind-driven */}
            {form.kind === 'non_drug' ? (
              <div style={styles.fieldGroup}>
                <label style={styles.label}>Category *</label>
                <select
                  style={styles.input}
                  value={form.category}
                  onChange={e => setForm({ ...form, category: e.target.value })}
                >
                  {CATEGORY_OPTIONS.map(c => (
                    <option key={c} value={c}>{CATEGORY_LABEL[c]}</option>
                  ))}
                </select>
              </div>
            ) : (
              <div style={styles.fieldGroup}>
                <label style={styles.label}>EPC class name *</label>
                <input
                  style={styles.input}
                  value={form.epc_class_name}
                  onChange={e => setForm({ ...form, epc_class_name: e.target.value })}
                  placeholder="e.g. Penicillin-class Antibacterial"
                />
              </div>
            )}

            {/* Sort order */}
            <div style={styles.fieldGroup}>
              <label style={styles.label}>Sort order</label>
              <input
                style={{ ...styles.input, maxWidth: '100px' }}
                type="number"
                value={form.sort_order}
                onChange={e => setForm({ ...form, sort_order: e.target.value })}
              />
            </div>
          </div>

          {/* Aliases — chips */}
          <div style={{ ...styles.fieldGroup, marginBottom: '16px' }}>
            <label style={styles.label}>
              Aliases <span style={ac.hint}>(shorthands that don't match the label, e.g. PCN)</span>
            </label>
            {form.aliases.length > 0 && (
              <div style={ac.chipWrap}>
                {form.aliases.map(a => (
                  <span key={a} style={ac.chip}>
                    {a}
                    <button type="button" style={ac.chipX}
                      onClick={() => removeAlias(a)} aria-label={`Remove ${a}`}>
                      <IconX size={11} stroke={2.5} />
                    </button>
                  </span>
                ))}
              </div>
            )}
            <div style={ac.aliasAddRow}>
              <input
                style={{ ...styles.input, flex: 1 }}
                value={aliasDraft}
                onChange={e => setAliasDraft(e.target.value)}
                onKeyDown={onAliasKeyDown}
                placeholder="Type an alias, press Enter"
              />
              <Button variant="neutral" size="sm" onClick={addAlias}>Add</Button>
            </div>
          </div>

          <div style={styles.formActions}>
            <Button onClick={handleSave} disabled={saving}>
              {saving ? 'Saving...' : editingId ? 'Save Changes' : 'Add Allergen'}
            </Button>
            <Button variant="secondary" onClick={cancelForm}>Cancel</Button>
          </div>
        </div>
      )}

      <div style={ac.controls}>
        <label style={ac.checkRow}>
          <input type="checkbox" checked={includeInactive}
            onChange={e => setIncludeInactive(e.target.checked)} />
          <span style={ac.checkLabel}>Show retired</span>
        </label>
      </div>

      {loading ? (
        <div style={styles.emptyRow}>Loading...</div>
      ) : error ? (
        <div style={{ ...styles.errorText, padding: '12px' }}>{error}</div>
      ) : (
        <table style={styles.table}>
          <thead>
            <tr>
              {['Label', 'Kind', 'Category / EPC class', 'Aliases', 'Status', 'Actions'].map(h => (
                <th key={h} style={styles.th}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {entries.length === 0 && (
              <tr><td colSpan={6} style={styles.emptyRow}>No allergens in the catalog yet.</td></tr>
            )}
            {entries.map(e => (
              <tr key={e.id} style={{ ...styles.tr, opacity: e.is_active ? 1 : 0.55 }}>
                <td style={styles.td}>{e.label}</td>
                <td style={{ ...styles.td, color: 'var(--color-text-muted)' }}>
                  {KIND_LABEL[e.kind] || e.kind}
                </td>
                <td style={{ ...styles.td, color: 'var(--color-text-muted)' }}>
                  {e.kind === 'non_drug'
                    ? (CATEGORY_LABEL[e.category] || e.category || '—')
                    : (e.epc_class_name || '—')}
                </td>
                <td style={{ ...styles.td, color: 'var(--color-text-muted)' }}>
                  {e.aliases && e.aliases.length ? e.aliases.join(', ') : '—'}
                </td>
                <td style={styles.td}>
                  <span style={e.is_active ? styles.badgeActive : styles.badgeInactive}>
                    {e.is_active ? 'Active' : 'Retired'}
                  </span>
                </td>
                <td style={styles.td}>
                  <div style={styles.actionGroup}>
                    <Button variant="neutral" size="sm" onClick={() => openEdit(e)}>
                      Edit
                    </Button>
                    <Button
                      variant={e.is_active ? 'caution' : 'neutral'}
                      size="sm"
                      onClick={() => toggleActive(e)}
                    >
                      {e.is_active ? 'Retire' : 'Restore'}
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

// Tab-local styles (chips, alias row, controls) — table/form/badges come from
// settingsShared.
const ac = {
  hint: { color: 'var(--color-text-muted)', fontWeight: 400, fontSize: '12px' },
  chipWrap: { display: 'flex', flexWrap: 'wrap', gap: '6px', marginBottom: '8px' },
  chip: { display: 'inline-flex', alignItems: 'center', gap: '4px', fontSize: '12px', fontWeight: 500, color: 'var(--color-primary)', backgroundColor: 'var(--color-accent-wash)', padding: '3px 6px 3px 9px', borderRadius: '4px', fontFamily: 'var(--font-family-base)' },
  chipX: { background: 'none', border: 'none', cursor: 'pointer', color: 'var(--color-primary)', padding: 0, display: 'inline-flex', alignItems: 'center', outline: 'none', appearance: 'none', WebkitAppearance: 'none' },
  aliasAddRow: { display: 'flex', gap: '8px', alignItems: 'center' },
  controls: { display: 'flex', alignItems: 'center', gap: '16px', margin: '4px 0 12px' },
  checkRow: { display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer' },
  checkLabel: { fontSize: 'var(--font-size-xs)', color: 'var(--color-text-secondary)', fontFamily: 'var(--font-family-base)' },
};